import { isEmpty } from 'lodash';
import { UploadDialogTypeResolver } from './category-upload-dialogs/upload-dialog-type-resolver';
import { AdminDocumentColumns } from './admin-document-columns';
import { UserPermissionsStorage } from '../../user-permissions/user-permissions-storage';
import { Component, ChangeDetectionStrategy } from '@angular/core';
import { Category, Report, CategoriesStorage, CategoryNaturalKey } from '../../documents';
import { Observable } from 'rxjs';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { map, flatMap, first, publishReplay, refCount, distinctUntilChanged, switchMap, tap } from 'rxjs/operators';
import { MdlDialogService, IMdlCustomDialogConfiguration } from '@angular-mdl/core';
import { ColDef } from 'ag-grid-community';
import { UrlBuilder } from '../../core/index';
import { UploadedDocumentsStorage } from './storages/uploaded-documents-storage';
import { UPLOAD_DLG_DATA_TOKEN, UploadDialogData } from './category-upload-dialogs/base/upload-dialog-data';
import { AdminDocumentsFilterItemsStorage } from './filter/admin-documents-filter-items-storage';
import { FILTER_ITEM_LOADER } from '../../documents/filter-items-loader';
import { DOCUMENTS_STORAGE } from '../../documents/documents-storage';
import { AdminDocumentsFilterResolver } from './filter/admin-documents-filter-resolver';
import { routeParams } from '../../shared/route-helpers';
import { FILTER_RESOLVER } from '../../documents/filter-resolver';
import { AdminDocumentsMandatoryFilterItemsStorage } from './filter/admin-documents-mandatory-filter-items-storage';
import { MandatoryFilter } from '../../documents/mandatory-filter';
import { siteFilter, protocolFilter, projectFilter, sponsorFilter } from '../../documents/document-filter-definitions';
import { KeyValuePair } from '../../documents/document-filter-item';

@Component({
    selector: 'admin-documents',
    template: require('./admin-documents.component.html'),
    styles: [require('./admin-documents.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: [
        { provide: FILTER_ITEM_LOADER, useExisting: AdminDocumentsFilterItemsStorage },
        { provide: DOCUMENTS_STORAGE, useExisting: UploadedDocumentsStorage },
        { provide: FILTER_RESOLVER, useExisting: AdminDocumentsFilterResolver },
    ]
})
export class AdminDocumentsComponent {

    public categories: Observable<Category[]>;
    public selectedCategory: Observable<Category>;
    public selectedCategoryId: Observable<CategoryNaturalKey>;
    public subCategories: Observable<Category[]>;
    public columns: Observable<ColDef[]>;
    public data: Observable<Report[]>;
    public canUploadCategory: Observable<boolean>;
    public readonly sponsorFilter = MandatoryFilter.Sponsor;
    public sponsors$: Observable<KeyValuePair[]>;
    public selectedSponsor$: Observable<KeyValuePair[]>;
    public readonly protocolFilter = MandatoryFilter.Protocol;
    public protocols$: Observable<KeyValuePair[]>;
    public selectedProtocol$: Observable<KeyValuePair[]>;
    public showProtocolWarning: Observable<boolean>;

    constructor(
        private route: ActivatedRoute,
        private categoriesStorage: CategoriesStorage,
        private permissionsStorage: UserPermissionsStorage,
        private mandatoryFilterItemsLoader: AdminDocumentsMandatoryFilterItemsStorage,
        private dialogService: MdlDialogService,
        private colDefs: AdminDocumentColumns,
        private dialogTypeResolver: UploadDialogTypeResolver,
        private urlBuilder: UrlBuilder,
        private router: Router,
    ) { }

    public ngOnInit() {
        this.categories = this.categoriesStorage.generalAndSiteSpecific;

        this.selectedCategoryId = this.route.params.pipe(
            map(x => x[routeParams.categoryId] as CategoryNaturalKey | undefined),
            distinctUntilChanged(),
            publishReplay(1),
            refCount()
        );

        this.selectedCategory = this.selectedCategoryId.pipe(
            flatMap(categoryId => this.categoriesStorage.findCategoryById(categoryId as CategoryNaturalKey)),
            publishReplay(1),
            refCount(),
        );

        this.subCategories = this.selectedCategory.pipe(map(c => c && c.children));
        this.columns = this.colDefs.getColumnDefinitions(this.selectedCategory);

        this.canUploadCategory = this.selectedCategoryId.pipe(
            switchMap(categoryId => this.permissionsStorage.userPermissions
                .pipe(map(permissions => permissions.canUploadCategory(categoryId)))
            )
        );

        this.showProtocolWarning = this.selectedCategory.pipe(map(cat => !isEmpty(cat) && cat.isLabReport()));

        this.sponsors$ = this.mandatoryFilterItemsLoader.getItemsFilter()
            .pipe(
                publishReplay(1),
                refCount()
            );
        
        this.selectedSponsor$ = this.sponsors$
            .pipe(
                flatMap((sponsorItems) =>
                    this.route.queryParams
                        .pipe(
                            map(p => {
                                const sponsorId = p[this.sponsorFilter] as string;
                                if (! sponsorId) {
                                    return null;
                                }
                                const sponsorItem = sponsorItems.filter(sp => !!sp).find(sponsor => sponsor.key === sponsorId);
                                return sponsorItem ? [sponsorItem] : null;
                            }), 
                            distinctUntilChanged(),
                    )));

        this.protocols$ = this.mandatoryFilterItemsLoader.getProtocolFilter()
            .pipe(
                publishReplay(1),
                refCount()
            );

        this.selectedProtocol$ = this.protocols$
            .pipe(
                flatMap((protocolItems) =>
                    this.route.queryParams
                        .pipe(
                            map(p => {
                                const protocolId = p[this.sponsorFilter] as string;
                                if (!protocolId) {
                                    return null;
                                }
                                const protocolItem = protocolItems.filter(sp => !!sp).find(protocol => protocol.key === protocolId);
                                return protocolItem ? [protocolItem] : null;
                            }),
                            distinctUntilChanged(),
                        )));
    }

    public categoryChanged(categoryId: CategoryNaturalKey) {
        this.urlBuilder.esa().admin().uploadDocuments(categoryId).navigate();
    }

    public addFiles() {
        this.selectedCategory.pipe(
            flatMap(sc => {
                const dialogType = this.dialogTypeResolver.getDialogType(sc);

                const dialogData: UploadDialogData<void> = {
                    category: sc,
                    subCategories$: this.subCategories
                };
                const configDialog: IMdlCustomDialogConfiguration = {
                    component: dialogType,
                    providers: [
                        {
                            provide: UPLOAD_DLG_DATA_TOKEN,
                            useValue: dialogData
                        }
                    ],
                    styles: {
                        'overflow': 'auto',
                        'max-height': '90vh'
                    },
                    isModal: true,
                    clickOutsideToClose: true
                };

                return this.dialogService.showCustomDialog(configDialog);
            }),
            first()).toPromise();
    }

    public onFilterItemsSelectorChange (mandatoryFilterItemValue: KeyValuePair[]) {
        const queryParam: Params = {};
        const sponsorKey = !isEmpty(mandatoryFilterItemValue) ? mandatoryFilterItemValue[0].key : null;
        queryParam[this.sponsorFilter] = sponsorKey;
        queryParam[siteFilter.queryParam] = null;
        queryParam[protocolFilter.queryParam] = null;
        queryParam[projectFilter.queryParam] = null;

        this.router.navigate(
            [], 
            {
              relativeTo: this.route,
              queryParams: queryParam, 
              queryParamsHandling: 'merge',
            });
    }
} 
